import { http } from "@/lib/api";
import {
  buildAvailabilityPatch,
  buildGoalPatchPayload,
} from "@/lib/trainingGoalOptions";

const normaliseError = (err) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "aborted", message: "Request cancelled" };
  }
  if (err?.code === "ERR_BACKEND_URL_MISSING") {
    return {
      code: "config",
      message: "Coach is not configured. Set REACT_APP_BACKEND_URL and reload.",
    };
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return {
      code: "network",
      message: "Cannot connect to the Endurava server.",
    };
  }
  const status = err?.response?.status;
  const rawBody = err?.response?.data;
  const bodyText = typeof rawBody === "string" ? rawBody.trim() : "";
  const detail = rawBody?.detail;
  const listMessage = Array.isArray(detail)
    ? detail.map((item) => item?.msg).filter(Boolean).join(" ")
    : "";
  const message =
    (typeof detail === "string" && detail)
    || detail?.message
    || listMessage
    || err?.response?.data?.message
    || bodyText
    || "Could not save your training goal.";
  if (status === 401) {
    return { code: "unauthenticated", status, message: "Your session has expired. Please sign in again." };
  }
  if (status === 400 || status === 409 || status === 422) {
    return { code: "invalid_request", status, message };
  }
  if (status === 404) {
    return { code: "not_found", status, message };
  }
  return { code: "unknown", status, message };
};

const wrapError = (err) => {
  const wrapped = new Error(normaliseError(err).message);
  wrapped.goalError = normaliseError(err);
  wrapped.status = err?.response?.status;
  throw wrapped;
};

const isNotFound = (err) => err?.response?.status === 404;

export const fetchAthleteProfile = async () => {
  try {
    const { data } = await http.get("/athlete/profile");
    return data;
  } catch (err) {
    if (isNotFound(err)) return null;
    wrapError(err);
  }
  return null;
};

export const ensureAthleteProfile = async () => {
  const existing = await fetchAthleteProfile();
  if (existing && existing.persistence_status !== "legacy_projection") {
    return existing;
  }
  try {
    const { data } = await http.post("/athlete/profile", {});
    return data;
  } catch (err) {
    if (err?.response?.status === 409) {
      return fetchAthleteProfile();
    }
    wrapError(err);
  }
  return null;
};

export const fetchAthleteGoals = async () => {
  try {
    const { data } = await http.get("/athlete/goals");
    return data;
  } catch (err) {
    wrapError(err);
  }
  return { items: [], count: 0, primary_goal_id: null };
};

export const createAthleteGoal = async (payload) => {
  try {
    const { data } = await http.post("/athlete/goals", payload);
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const patchAthleteGoal = async (goalId, payload) => {
  try {
    const { data } = await http.patch(
      `/athlete/goals/${encodeURIComponent(goalId)}`,
      payload,
    );
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const setPrimaryAthleteGoal = async (
  goalId,
  { expectedProfileRevision, expectedGoalRevision },
) => {
  try {
    const { data } = await http.post(
      `/athlete/goals/${encodeURIComponent(goalId)}/set-primary`,
      {
        expected_profile_revision: expectedProfileRevision,
        expected_goal_revision: expectedGoalRevision,
      },
    );
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const fetchSportProfile = async (sport) => {
  try {
    const { data } = await http.get(
      `/athlete/sport-profiles/${encodeURIComponent(sport)}`,
    );
    return data;
  } catch (err) {
    if (isNotFound(err)) return null;
    wrapError(err);
  }
  return null;
};

export const patchSportProfile = async (sport, payload) => {
  try {
    const { data } = await http.patch(
      `/athlete/sport-profiles/${encodeURIComponent(sport)}`,
      payload,
    );
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const ensureSportProfile = async (sport, extra = {}) => {
  const existing = await fetchSportProfile(sport);
  if (existing) {
    if (
      extra.experience_level
      && extra.experience_level !== existing.experience_level
    ) {
      return patchSportProfile(sport, {
        expected_revision: existing.revision,
        experience_level: extra.experience_level,
      });
    }
    return existing;
  }
  try {
    const { data } = await http.post("/athlete/sport-profiles", {
      sport,
      ...extra,
    });
    return data;
  } catch (err) {
    if (err?.response?.status === 409) {
      return fetchSportProfile(sport);
    }
    wrapError(err);
  }
  return null;
};

export const setPrimarySport = async (
  sport,
  { expectedProfileRevision, expectedSportProfileRevision },
) => {
  try {
    const { data } = await http.post(
      `/athlete/sport-profiles/${encodeURIComponent(sport)}/set-primary`,
      {
        expected_profile_revision: expectedProfileRevision,
        expected_sport_profile_revision: expectedSportProfileRevision,
      },
    );
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const fetchAvailability = async () => {
  try {
    const { data } = await http.get("/athlete/availability");
    return data;
  } catch (err) {
    if (isNotFound(err)) return null;
    wrapError(err);
  }
  return null;
};

export const createAvailability = async (payload) => {
  try {
    const { data } = await http.post("/athlete/availability", payload);
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const patchAvailability = async (payload) => {
  try {
    const { data } = await http.patch("/athlete/availability", payload);
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

const canonicalSportsForGoal = (goalPayload = {}, primarySport) => {
  const fromGoal = [
    ...(Array.isArray(goalPayload.sports) ? goalPayload.sports : []),
    ...(goalPayload.sport ? [goalPayload.sport] : []),
  ];
  if (fromGoal.length > 0) return [...new Set(fromGoal)];
  if (primarySport === "triathlon") return ["swimming", "cycling", "running"];
  if (primarySport === "duathlon") return ["running", "cycling"];
  if (primarySport) return [primarySport];
  return [];
};

const canonicalPrimarySport = (primarySport, sports) => {
  if (primarySport === "triathlon") return "cycling";
  if (primarySport === "duathlon") return "running";
  if (primarySport && primarySport !== "triathlon" && primarySport !== "duathlon") {
    return primarySport;
  }
  return sports[0] || null;
};

const rollbackFailedQuickGoalUpdate = async ({
  profile,
  previousPrimaryGoalId,
  failedGoalId,
}) => {
  if (!failedGoalId || !profile) return profile;
  let nextProfile = profile;
  if (
    previousPrimaryGoalId
    && previousPrimaryGoalId !== failedGoalId
    && profile.primary_goal_id === failedGoalId
  ) {
    const previousGoal = await fetchAthleteGoals().then(
      (response) => (response?.items || []).find(
        (item) => item.goal_id === previousPrimaryGoalId,
      ),
    );
    if (previousGoal?.goal_id) {
      const restored = await setPrimaryAthleteGoal(previousGoal.goal_id, {
        expectedProfileRevision: profile.revision,
        expectedGoalRevision: previousGoal.revision,
      });
      nextProfile = restored?.profile || (await fetchAthleteProfile()) || profile;
    }
  }
  const failedGoal = (await fetchAthleteGoals()).items?.find(
    (item) => item.goal_id === failedGoalId,
  );
  if (failedGoal?.goal_id && failedGoal.status !== "archived") {
    await patchAthleteGoal(failedGoal.goal_id, {
      expected_revision: failedGoal.revision,
      status: "archived",
    });
  }
  return nextProfile;
};

export const saveAuthoritativeTrainingGoal = async ({
  goalPayload,
  existingGoal = null,
  primarySport,
  availabilityInputs = null,
  existingAvailability = null,
  experienceLevel,
}) => {
  let profile = await ensureAthleteProfile();
  if (!profile) {
    throw new Error("Create your athlete profile before setting a goal.");
  }

  const sports = canonicalSportsForGoal(goalPayload, primarySport);
  const sportExtras = experienceLevel ? { experience_level: experienceLevel } : {};
  let lastSportProfile = null;
  for (const sport of sports) {
    lastSportProfile = await ensureSportProfile(sport, sportExtras);
  }
  const nextPrimarySport = canonicalPrimarySport(primarySport, sports);
  if (
    nextPrimarySport
    && lastSportProfile
    && profile.primary_sport !== nextPrimarySport
  ) {
    const primarySportProfile = await ensureSportProfile(nextPrimarySport);
    const updated = await setPrimarySport(nextPrimarySport, {
      expectedProfileRevision: profile.revision,
      expectedSportProfileRevision: primarySportProfile.revision,
    });
    profile = updated?.profile || (await fetchAthleteProfile()) || profile;
  }

  const previousPrimaryGoalId = profile.primary_goal_id || null;
  const goalPlan = buildGoalPatchPayload(existingGoal, goalPayload);
  let createdGoalId = null;

  const availabilityPlan = availabilityInputs
    ? buildAvailabilityPatch({
      existingAvailability,
      ...availabilityInputs,
    })
    : { payload: null, changed: false };

  let savedAvailability = existingAvailability;
  if (availabilityPlan.changed && availabilityPlan.payload) {
    if (!existingAvailability) {
      savedAvailability = await createAvailability(availabilityPlan.payload);
    } else if (existingAvailability.status !== "archived") {
      savedAvailability = await patchAvailability(availabilityPlan.payload);
    }
  }

  let goal = existingGoal || null;
  try {
    if (goalPlan.requiresCreate) {
      goal = await createAthleteGoal(goalPayload);
      createdGoalId = goal?.goal_id || null;
      profile = (await fetchAthleteProfile()) || profile;
      if (goal && !goal.is_primary) {
        const primary = await setPrimaryAthleteGoal(goal.goal_id, {
          expectedProfileRevision: profile.revision,
          expectedGoalRevision: goal.revision,
        });
        goal = primary?.goal || goal;
        profile = primary?.profile || profile;
      }
    } else if (existingGoal?.goal_id && goalPlan.changed) {
      goal = await patchAthleteGoal(existingGoal.goal_id, {
        expected_revision: existingGoal.revision,
        ...goalPlan.patch,
        ...(goalPlan.clearFields?.length
          ? { clear_fields: goalPlan.clearFields }
          : {}),
      });
      profile = (await fetchAthleteProfile()) || profile;
    } else if (existingGoal?.goal_id) {
      goal = existingGoal;
    } else {
      goal = await createAthleteGoal(goalPayload);
      createdGoalId = goal?.goal_id || null;
      profile = (await fetchAthleteProfile()) || profile;
      if (goal && !goal.is_primary) {
        const primary = await setPrimaryAthleteGoal(goal.goal_id, {
          expectedProfileRevision: profile.revision,
          expectedGoalRevision: goal.revision,
        });
        goal = primary?.goal || goal;
        profile = primary?.profile || profile;
      }
    }
  } catch (err) {
    err.previewWrites = { goal, availability: savedAvailability, profile };
    if (createdGoalId) {
      profile = await rollbackFailedQuickGoalUpdate({
        profile,
        previousPrimaryGoalId,
        failedGoalId: createdGoalId,
      });
    }
    throw err;
  }

  return { goal, profile, availability: savedAvailability };
};
